import React from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { CONTACT } from '../../config/contact';

const PaymentFailed = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const state = location.state || {};

    const errorMessage =
        state.message ||
        searchParams.get('message') ||
        searchParams.get('ErrorMessage') ||
        searchParams.get('error') ||
        (searchParams.get('error_code') === 'missing_tracking_id' ? 'Missing payment tracking information. Please try again.' : '') ||
        'Payment could not be completed';
    const txRef = state.tx_ref || searchParams.get('tx_ref') || searchParams.get('OrderMerchantReference') || searchParams.get('reference');

    const handleRetry = () => {
        // We don't have the original plan/billing data here, so we can't
        // re-initialize a payment directly. Send the user back to pricing
        // to start a fresh checkout.
        navigate('/pricing');
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
            <div className="max-w-2xl w-full">
                {/* Failure Card */}
                <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-12 text-center border border-gray-200 dark:border-gray-700 shadow-xl">
                    {/* Error Icon */}
                    <div className="relative mx-auto w-24 h-24 mb-6">
                        <div className="absolute inset-0 bg-red-500 rounded-full animate-pulse opacity-20"></div>
                        <div className="relative w-full h-full bg-red-500 rounded-full flex items-center justify-center">
                            <XCircleIcon className="w-12 h-12 text-white" />
                        </div>
                    </div>

                    {/* Error Message */}
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
                        Payment Failed
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                        {errorMessage}
                    </p>

                    {/* Transaction Reference */}
                    {txRef && (
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-2xl p-4 mb-8">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Reference: <span className="font-mono text-gray-900 dark:text-white">{txRef}</span>
                            </p>
                        </div>
                    )}

                    {/* Common Reasons */}
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-2xl p-6 mb-8 text-left">
                        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                            Common Reasons for Payment Failure:
                        </h3>
                        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                            <li className="flex items-start gap-2">
                                <span className="text-red-500 mt-0.5">•</span>
                                <span>Insufficient funds in your account</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-500 mt-0.5">•</span>
                                <span>Incorrect card details or expired card</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-500 mt-0.5">•</span>
                                <span>Transaction declined by your bank</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-500 mt-0.5">•</span>
                                <span>Network timeout or connection issue</span>
                            </li>
                        </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 mb-6">
                        <button
                            onClick={handleRetry}
                            className="flex-1 py-4 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2"
                        >
                            <ArrowPathIcon className="w-5 h-5" />
                            Try Again
                        </button>
                        <button
                            onClick={() => navigate('/pricing')}
                            className="flex-1 py-4 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            Back to Pricing
                        </button>
                    </div>

                    {/* Help Text */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                            💡 <strong>Tip:</strong> If the issue persists, please contact your bank or try a different payment method.
                        </p>
                    </div>
                </div>

                {/* Support Section */}
                <div className="mt-8 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                        Need assistance?
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
                        <a
                            href={`mailto:${CONTACT.email}`}
                            className="text-teal-600 dark:text-teal-400 hover:underline font-medium"
                        >
                            📧 {CONTACT.email}
                        </a>
                        <span className="hidden sm:inline text-gray-400">|</span>
                        <a
                            href={`tel:${CONTACT.phoneTel}`}
                            className="text-teal-600 dark:text-teal-400 hover:underline font-medium"
                        >
                            📞 {CONTACT.phoneDisplay}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentFailed;
